import { StatusIndicatorProps } from "@amzn/awsui-components-react/polaris/status-indicator/internal";
import Icon from "@amzn/awsui-components-react/polaris/icon";
import React from "react";
import {
  AsinAudit,
  Requester,
  ValidationDetailWithAudits,
} from "../../server/asin-central/model";

export type ValidationAttributeOverallStatus =
  | "pass"
  | "pass_exception"
  | "fail"
  | "not_applicable";

export type ValidationAttributeDetailStepStatus =
  | ValidationAttributeOverallStatus
  | "exception"
  | "pass_exception_one_time";
export interface ValidationAttributeDetailStep {
  step: string;
  result: ValidationAttributeDetailStepStatus;
  resultAudit: AsinAudit;
  reason?: string;
  passExceptionReason?: string;
  temporary?: boolean;
  hint?: string;
  passExceptionRequester?: {
    identity: string;
    type: string;
  };
  lastUpdateTime?: Date;
  correctedValue?: string;
}

export interface ValidationAttribute {
  overallResult: ValidationAttributeOverallStatus;
  detail: ValidationAttributeDetailStep[];
}

export function getIconForValidationAttribute(
  status: ValidationAttributeDetailStepStatus
) {
  return status === "pass" ? (
    <Icon name="status-positive" variant="success" />
  ) : status === "pass_exception" ? (
    <Icon name="status-positive" variant="success" />
  ) : status === "pass_exception_one_time" ? (
    <Icon name="status-positive" variant="success" />
  ) : status === "fail" ? (
    <Icon name="status-negative" variant="error" />
  ) : status === "exception" ? (
    <Icon name="status-in-progress" variant="normal" />
  ) : status === "not_applicable" ? (
    <Icon name="status-stopped" variant="subtle" />
  ) : (
    <Icon name="status-stopped" variant="subtle" />
  );
}

export function statusIconTypeFromValidationAttribute(
  status: ValidationAttributeDetailStepStatus
): StatusIndicatorProps.Type {
  let indicatorType = "stopped";
  switch (status) {
    case "pass":
      indicatorType = "success";
      break;
    case "pass_exception":
      indicatorType = "success";
      break;
    case "pass_exception_one_time":
      indicatorType = "success";
      break;
    case "not_applicable":
      indicatorType = "stopped";
      break;
    case "fail":
      indicatorType = "error";
      break;
    case "exception":
      indicatorType = "warning";
      break;
  }

  return indicatorType as StatusIndicatorProps.Type;
}

export const VALIDATION_ATTRIBUTE_RESULT: { [result: string]: string } = {
  pass: "Pass",
  fail: "Fail",
  pass_exception: "Pass (Exception)",
  pass_exception_one_time: "One-Time Pass (Exception)",
  not_applicable: "Not Applicable",
  exception: "Exception",
};

export const VALIDATION_ATTRIBUTE_OVERALL_RESULT: { [result: string]: string } =
  {
    pass: "Pass",
    fail: "Fail",
    pass_exception: "Pass (w/ Exceptions)",
    not_applicable: "Not Applicable",
    exception: "Exception",
  };

export const VALIDATION_ATTRIBUTE_NAME_MAP: {
  [attributeName: string]: string;
} = {
  validationOverallResultManualDGL: "DGL",
  validationOverallResultManualRL: "RL",
  validationOverallResultManualInstock: "Instock",
  validationOverallResultManualOX: "OX",
  validationOverallResultManualGTM: "GTM",
  validationOverallResultManualCS: "CS",
  validationOverallResultHattrick: "Hattrick",
  validationOverallResultLVS: "LVS",
};

export interface IValidationService {
  serviceId: string;
  serviceName: string;
  isManual: boolean;
  overallAttributeName: string;
  detailAttributeName: string;
}

export const VALIDATION_SERVICES: IValidationService[] = [
  {
    serviceId: "LVS",
    serviceName: "LVS",
    isManual: false,
    overallAttributeName: "validationOverallResultLVS",
    detailAttributeName: "validationDetailResultLVS",
  },
  {
    serviceId: "Hattrick",
    serviceName: "Hattrick",
    isManual: false,
    overallAttributeName: "validationOverallResultHattrick",
    detailAttributeName: "validationDetailResultHattrick",
  },
  {
    serviceId: "ManualDGL",
    serviceName: "DGL",
    isManual: true,
    overallAttributeName: "validationOverallResultManualDGL",
    detailAttributeName: "validationDetailResultManualDGL",
  },
  {
    serviceId: "ManualRL",
    serviceName: "RL",
    isManual: true,
    overallAttributeName: "validationOverallResultManualRL",
    detailAttributeName: "validationDetailResultManualRL",
  },
  {
    serviceId: "ManualInstock",
    serviceName: "Instock",
    isManual: true,
    overallAttributeName: "validationOverallResultManualInstock",
    detailAttributeName: "validationDetailResultManualInstock",
  },
  {
    serviceId: "ManualOX",
    serviceName: "OX",
    isManual: true,
    overallAttributeName: "validationOverallResultManualOX",
    detailAttributeName: "validationDetailResultManualOX",
  },
  {
    serviceId: "ManualGTM",
    serviceName: "GTM",
    isManual: true,
    overallAttributeName: "validationOverallResultManualGTM",
    detailAttributeName: "validationDetailResultManualGTM",
  },
  {
    serviceId: "ManualCS",
    serviceName: "CS",
    isManual: true,
    overallAttributeName: "validationOverallResultManualCS",
    detailAttributeName: "validationDetailResultManualCS",
  },
];

export function getIValidationServiceById(id: string) {
  switch (id) {
    case "LVS":
      return VALIDATION_SERVICES[0];
    case "Hattrick":
      return VALIDATION_SERVICES[1];
    case "ManualDGL":
      return VALIDATION_SERVICES[2];
    case "ManualRL":
      return VALIDATION_SERVICES[3];
    case "ManualInstock":
      return VALIDATION_SERVICES[4];
    case "ManualOX":
      return VALIDATION_SERVICES[5];
    case "ManualGTM":
      return VALIDATION_SERVICES[6];
    case "ManualCS":
      return VALIDATION_SERVICES[7];
    default:
      return null;
  }
}
